import { isSameDay, isSameMonth, isSameYear, isToday, isYesterday } from "date-fns";
import { format, toZonedTime } from "date-fns-tz";
import { getUILanguage, translation } from "../package/albertine-i18n";

export const defaultSeparator = " · ";
export const timeDateSeparator = " - ";

function removeSpaces(str: string) {
    return str.replaceAll(" ", "");
}

const excludeUndefined = Boolean as unknown as <T>(x: T | undefined) => x is T;

export function combineWith(
    arr: (string | undefined)[],
    separator: string,
): string | undefined {
    const onlyValues = arr.filter(excludeUndefined);
    return onlyValues.length !== 0 ? onlyValues.join(separator) : undefined;
}

function getDateFormat(props: {
    showDay: boolean;
    showYear: boolean;
    showTime: boolean;
    separator?: string;
}) {
    const { showDay, showYear, showTime, separator = defaultSeparator } = props;

    const dayPrefix = showDay ? "E " : "";
    const yearSuffix = showYear ? " yyyy" : "";
    const timeSuffix = showTime ? "HH:mm" : "";
    const separatorOrEmpty = showTime ? separator : "";

    return `${dayPrefix}d MMM${yearSuffix}${separatorOrEmpty}${timeSuffix}`;
}

function formattedZonedTime(
    timeISO: string,
    timeZone: string,
    showDay = true,
    showYear = true,
    separator: string = defaultSeparator,
    timeFormat?: string,
) {
    return format(
        toZonedTime(timeISO, timeZone),
        timeFormat ??
            getDateFormat({
                showDay,
                showYear,
                showTime: true,
                separator,
            }),
        {
            timeZone,
            locale: getUILanguage().dateFnsLocale,
        },
    );
}

/**
 * Formats a single date/time with flexible options.
 *
 * @param timeISO - ISO string for the time
 * @param timezone - Timezone for the time
 * @param options - Formatting options
 * @returns Formatted string
 *
 * @example
 * formatDateTime("2023-03-01T14:00:00Z", "UTC", { showDay: true, showTime: true })
 * // => "Wed, 1 Mar · 14:00"
 */
function formatDateTime(
    timeISO: string,
    timezone: string,
    options: {
        showDay?: boolean;
        showYear?: boolean;
        showTime?: boolean;
        now?: Date;
    } = {},
): string {
    const { showDay = true, showYear, showTime = false, now = new Date() } = options;

    const date = toZonedTime(timeISO, timezone);
    const locale = getUILanguage().dateFnsLocale;

    // Determine if year should be shown
    const includeYear = showYear ?? !isSameYear(date, now);

    // Build format string
    const dayPrefix = showDay ? "E, " : "";
    const yearSuffix = includeYear ? " yyyy" : "";
    const timeSuffix = showTime ? `${defaultSeparator}HH:mm` : "";

    const formatStr = `${dayPrefix}d MMM${yearSuffix}${timeSuffix}`;

    return format(date, formatStr, { timeZone: timezone, locale });
}

/**
 * Formats a time interval following iOS DateFormatter.formatTimeInterval pattern.
 * Handles same day, same month, different month, and different year scenarios.
 *
 * @param startTime - ISO string for start time
 * @param startTimezone - Timezone for start time
 * @param endTime - ISO string for end time
 * @param endTimezone - Timezone for end time
 * @param options - Formatting options
 * @returns Object with formatted parts and full string
 *
 * @example
 * // Same day with times
 * formatTimeInterval("2023-03-01T14:00:00Z", "UTC", "2023-03-01T16:00:00Z", "UTC", { showTime: true })
 * // => { start: "Wed, 1 Mar · 14:00", end: "16:00", full: "Wed, 1 Mar · 14:00 - 16:00" }
 *
 * @example
 * // Same month without times
 * formatTimeInterval("2023-03-01T12:00:00Z", "UTC", "2023-03-05T12:00:00Z", "UTC")
 * // => { start: "Wed, 1", end: "Sun, 5 Mar", full: "Wed, 1 · Sun, 5 Mar" }
 */
function formatTimeInterval(
    startTime: string,
    startTimezone: string,
    endTime: string,
    endTimezone: string,
    options: {
        showTime?: boolean;
        now?: Date;
    } = {},
): { start: string; end: string; full: string } {
    const { showTime = false, now = new Date() } = options;

    const start = toZonedTime(startTime, startTimezone);
    const end = toZonedTime(endTime, endTimezone);

    const locale = getUILanguage().dateFnsLocale;

    // Same day
    if (isSameDay(start, end)) {
        const sameYear = isSameYear(start, now);
        const dateFormat = sameYear ? "E, d MMM" : "E, d MMM yyyy";
        const datePart = format(start, dateFormat, { locale });

        const startTimeStr = format(start, "HH:mm", { locale });
        const endTimeStr = format(end, "HH:mm", { locale });

        // If showing time and times are different, show time range
        if (showTime && startTimeStr !== endTimeStr) {
            return {
                start: `${datePart}${defaultSeparator}${startTimeStr}`,
                end: endTimeStr,
                full: `${datePart}${defaultSeparator}${startTimeStr}${timeDateSeparator}${endTimeStr}`,
            };
        }

        // Same time or not showing times, just show date
        return {
            start: datePart,
            end: datePart,
            full: datePart,
        };
    }

    // Different days - check if same month/year
    const sameMonth = isSameMonth(start, end);
    const sameYear = isSameYear(start, end);
    const startIsCurrentYear = isSameYear(start, now);
    const endIsCurrentYear = isSameYear(end, now);

    // Build base format strings based on date relationships
    let startDateFormat: string;
    let endDateFormat: string;

    if (sameMonth && sameYear) {
        // Same month: "Wed, 1" and "Sun, 5 Mar"
        startDateFormat = "E, d";
        endDateFormat = endIsCurrentYear ? "E, d MMM" : "E, d MMM yyyy";
    } else if (sameYear) {
        // Same year, different month: "Wed, 1 Mar" and "Sat, 1 Apr"
        startDateFormat = "E, d MMM";
        endDateFormat = endIsCurrentYear ? "E, d MMM" : "E, d MMM yyyy";
    } else {
        // Different year
        startDateFormat = startIsCurrentYear ? "E, d MMM" : "E, d MMM yyyy";
        endDateFormat = "E, d MMM yyyy";
    }

    // Format dates
    let startPart = format(start, startDateFormat, { locale });
    let endPart = format(end, endDateFormat, { locale });

    // Add times if requested
    if (showTime) {
        const startTimeStr = format(start, "HH:mm", { locale });
        const endTimeStr = format(end, "HH:mm", { locale });
        startPart = `${startPart}${defaultSeparator}${startTimeStr}`;
        endPart = `${endPart}${defaultSeparator}${endTimeStr}`;
    }

    return {
        start: startPart,
        end: endPart,
        full: `${startPart}${timeDateSeparator}${endPart}`,
    };
}

const textFormat = {
    booking: {
        time: (
            startTime: string | undefined,
            startTimezone: string | undefined,
            endTime: string | undefined,
            endTimezone: string | undefined,
            options: {
                showTime?: boolean;
                now?: Date;
            } = {},
        ): {
            bookingStartTime: string | undefined;
            bookingEndTime: string | undefined;
            fullDateTimeString: string | undefined;
        } => {
            const { showTime = true } = options;
            const now = options.now;

            // Range with both start and end
            if (startTime && startTimezone && endTime && endTimezone) {
                const formatted = formatTimeInterval(
                    startTime,
                    startTimezone,
                    endTime,
                    endTimezone,
                    { showTime, now },
                );

                return {
                    bookingStartTime: formatted.start,
                    bookingEndTime: formatted.end,
                    fullDateTimeString: formatted.full,
                };
            }

            // Single start time only
            if (startTime && startTimezone) {
                const bookingStartTime = formatDateTime(startTime, startTimezone, {
                    showDay: true,
                    showTime,
                    now,
                });
                return {
                    bookingStartTime,
                    bookingEndTime: undefined,
                    fullDateTimeString: bookingStartTime,
                };
            }

            return {
                bookingStartTime: undefined,
                bookingEndTime: undefined,
                fullDateTimeString: undefined,
            };
        },

        startDateAndTime: (
            startTime: string | undefined,
            startTimezone: string | undefined,
            separator: string = defaultSeparator,
            dateFormat = "EEEE, MMMM d",
            timeFormat = "HH:mm",
        ): {
            bookingStartDate: string | undefined;
            bookingStartTime: string | undefined;
        } => {
            if (startTime && startTimezone) {
                const startIsCurrentYear = isSameYear(startTime, new Date());

                return {
                    bookingStartDate: formattedZonedTime(
                        startTime,
                        startTimezone,
                        true,
                        !startIsCurrentYear,
                        separator,
                        dateFormat,
                    ),
                    bookingStartTime: formattedZonedTime(
                        startTime,
                        startTimezone,
                        false,
                        false,
                        separator,
                        timeFormat,
                    ),
                };
            }

            return { bookingStartDate: undefined, bookingStartTime: undefined };
        },

        telephoneLink: (phoneNumber: string) => `tel:${removeSpaces(phoneNumber)}`,
        numberOfGuests: (numberOfGuests: number | undefined) =>
            numberOfGuests
                ? translation("number-of-quests", {
                      count: numberOfGuests,
                  })
                : undefined,
        numberOfNights: (numberOfNights: number | undefined) =>
            numberOfNights
                ? translation("number-of-nights", {
                      count: numberOfNights,
                  })
                : undefined,
    },

    proposal: {
        time: (
            startDate: string | undefined,
            startTime: string | undefined,
            endDate: string | undefined,
            endTime: string | undefined,
            options: {
                showTime?: boolean;
                now?: Date;
            } = {},
        ): {
            requestStartTime: string | undefined;
            requestEndTime: string | undefined;
            fullDateTimeString: string | undefined;
        } => {
            const { showTime = !!startTime || !!endTime, now } = options;

            if (!startDate) {
                return {
                    requestStartTime: undefined,
                    requestEndTime: undefined,
                    fullDateTimeString: undefined,
                };
            }

            if (!endDate) {
                const requestStartTime = formatDateTime(startDate, "UTC", {
                    showDay: true,
                    showTime,
                    now,
                });

                return {
                    requestStartTime,
                    requestEndTime: undefined,
                    fullDateTimeString: requestStartTime,
                };
            }

            // Convert to ISO strings for formatTimeInterval
            const startISO = `${startDate}T${startTime ?? "12:00"}:00Z`;
            const endISO = `${endDate}T${endTime ?? "12:00"}:00Z`;

            // Use UTC timezone for proposals (they don't have timezone info)
            const formatted = formatTimeInterval(startISO, "UTC", endISO, "UTC", {
                showTime,
                now,
            });

            return {
                requestStartTime: formatted.start,
                requestEndTime: formatted.end,
                fullDateTimeString: formatted.full,
            };
        },

        numberOfGuests: (numberOfGuests: number | undefined) =>
            numberOfGuests
                ? translation("number-of-quests", {
                      count: numberOfGuests,
                  })
                : undefined,
    },

    chat: {
        datePill: (date: Date) => {
            if (isToday(date)) {
                return translation("today");
            }

            if (isYesterday(date)) {
                return translation("yesterday");
            }

            const yearSuffix = isSameYear(date, new Date()) ? "" : " yyyy";

            return format(date, `d MMM${yearSuffix}`, {
                locale: getUILanguage().dateFnsLocale,
            });
        },
    },

    member: {
        nameInitials: (fullName: string) => {
            const split = fullName.split(" ");

            if (split.length === 0) {
                return "";
            }

            const lastSplit = split.length > 1 ? split[split.length - 1] : undefined;

            return `${split[0].charAt(0).toUpperCase()}${lastSplit ? lastSplit.charAt(0).toUpperCase() : ""}`;
        },
        membership: (
            date: Date | string | number,
            formatType: "monthYear" | "dayMonthYear",
        ) => {
            const formats = {
                monthYear: "MMMM yyyy",
                dayMonthYear: "dd MMM yyyy",
            };

            return format(date, formats[formatType], {
                locale: getUILanguage().dateFnsLocale,
            });
        },
    },

    // Export utilities for testing and direct use
    formatDateTime,
    formatTimeInterval,
};

export default textFormat;
